import React, { useState, useCallback } from 'react';
import { useTheme, THEMES } from '../context/ThemeContext';
import { Check, Palette, Sliders, Type, Grid3X3, Plus, Trash2, Star, Sun, Moon } from 'lucide-react';
import { toast } from 'sonner';

// ── Mini App Preview ───────────────────────────────────────────────────────
function ThemePreview({ theme, isActive, isPreviewing }) {
  const t = theme.preview;
  const isLight = theme.category === 'light';
  return (
    <div
      className="w-full aspect-video rounded-lg overflow-hidden relative border-2 transition-all duration-200"
      style={{
        background: t.bg,
        borderColor: isActive ? t.accent : isPreviewing ? `${t.accent}80` : `${isLight ? '#E5E7EB' : '#1E2028'}`,
        boxShadow: isActive ? `0 0 0 3px ${t.accent}30` : 'none',
      }}
    >
      {/* Mini sidebar */}
      <div className="absolute left-0 top-0 bottom-0 w-8 flex flex-col items-center gap-1.5 pt-2"
           style={{ background: theme.vars['--cb-sidebar-bg'] || t.bg, borderRight: `1px solid ${theme.vars['--cb-sidebar-bdr'] || t.surface}` }}>
        <div className="w-4 h-4 rounded-sm" style={{ background: t.accent }} />
        {[1,2,3,4].map(i => (
          <div key={i} className="w-3 h-1.5 rounded-sm opacity-40" style={{ background: t.text }} />
        ))}
      </div>
      {/* Main content */}
      <div className="absolute left-8 right-0 top-0 bottom-0 p-2 flex flex-col gap-1.5">
        {/* Stat row */}
        <div className="flex gap-1">
          {['48', '12', '6'].map((n, i) => (
            <div key={i} className="flex-1 rounded px-1 py-1" style={{ background: t.surface, border: `1px solid ${theme.vars['--cb-border'] || '#1E2028'}` }}>
              <div className="text-[5px] font-bold" style={{ color: t.text }}>{n}</div>
              <div className="text-[4px] opacity-50" style={{ color: t.text }}>scenes</div>
            </div>
          ))}
        </div>
        {/* Scene rows */}
        {[['01','INT. CAFÉ','DAY'], ['02','EXT. PARK','NIGHT'], ['03','INT. OFFICE','DAY']].map(([n, h, t2], i) => (
          <div key={i} className="flex items-center gap-1 px-1 py-0.5 rounded" style={{ background: i % 2 === 0 ? t.surface : 'transparent' }}>
            <span className="text-[4px] font-mono font-bold px-0.5 rounded" style={{ background: `${t.accent}20`, color: t.accent }}>{n}</span>
            <span className="text-[4px] flex-1" style={{ color: t.text, opacity: 0.8 }}>{h}</span>
            <span className="text-[4px]" style={{ color: t.accent, opacity: 0.6 }}>{t2}</span>
          </div>
        ))}
      </div>
      {/* Active badge */}
      {isActive && (
        <div className="absolute top-1 right-1 rounded-full p-0.5" style={{ background: t.accent }}>
          <Check className="h-2 w-2 text-black" />
        </div>
      )}
    </div>
  );
}

// ── Theme Card ─────────────────────────────────────────────────────────────
function ThemeCard({ theme, isActive, onApply, onPreview, onLeave }) {
  return (
    <div
      className="rounded-xl p-4 cursor-pointer transition-all duration-200 group hover:translate-y-[-2px]"
      style={{
        background: 'var(--cb-surface)',
        border: `1px solid ${isActive ? 'var(--cb-accent)' : 'var(--cb-border)'}`,
        boxShadow: isActive ? '0 0 0 1px var(--cb-accent)' : 'none',
      }}
      onMouseEnter={() => onPreview(theme.id)}
      onMouseLeave={onLeave}
      onClick={() => onApply(theme.id)}
      data-testid={`theme-card-${theme.id}`}
    >
      <ThemePreview theme={theme} isActive={isActive} isPreviewing={false} />
      <div className="mt-3 flex items-start justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-sm" style={{ color: 'var(--cb-text)' }}>{theme.name}</span>
            {isActive && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: 'var(--cb-accent)', color: 'black' }}>Active</span>}
          </div>
          <p className="text-[11px] mt-0.5 leading-relaxed" style={{ color: 'var(--cb-text-dim)' }}>{theme.description}</p>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-1.5">
        {/* Swatch dots */}
        {[theme.preview.bg, theme.preview.surface, theme.preview.accent, theme.preview.text].map((c, i) => (
          <div key={i} className="h-3 w-3 rounded-full border border-black/10" style={{ background: c }} />
        ))}
        <span className="ml-auto text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full"
              style={{ background: theme.category === 'light' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.2)', color: 'var(--cb-text-dim)' }}>
          {theme.category}
        </span>
      </div>
    </div>
  );
}

// ── Accent Color Picker ────────────────────────────────────────────────────
const PRESET_ACCENTS = [
  { label: 'Gold', color: '#F5A623' }, { label: 'Amber', color: '#D4A017' },
  { label: 'Blue', color: '#58A6FF' }, { label: 'Teal', color: '#00E5A0' },
  { label: 'Violet', color: '#A78BFA' }, { label: 'Rose', color: '#FB7185' },
  { label: 'Emerald', color: '#34D399' }, { label: 'Sky', color: '#38BDF8' },
  { label: 'Orange', color: '#FB923C' }, { label: 'Lime', color: '#A3E635' },
];

// ── Main Page ──────────────────────────────────────────────────────────────
export default function ThemeMarketplace() {
  const {
    activeId, allThemes, applyTheme, previewTheme, clearPreview,
    customAccent, setCustomAccent,
    density, setDensity,
    fontScale, setFontScale,
    userThemes, saveUserTheme, deleteUserTheme,
  } = useTheme();

  const [activeTab, setActiveTab] = useState('themes'); // themes | customize | custom
  const [newThemeName, setNewThemeName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createBaseId, setCreateBaseId] = useState('studio-dark');
  const [createAccent, setCreateAccent] = useState('#F5A623');

  const builtIn = allThemes.filter(t => !t.isCustom);
  const custom = userThemes;

  const handleApply = useCallback((id) => {
    applyTheme(id, customAccent);
    toast.success(`Theme applied: ${allThemes.find(t => t.id === id)?.name}`);
  }, [applyTheme, customAccent, allThemes]);

  const handleCreateTheme = () => {
    if (!newThemeName.trim()) { toast.error('Enter a theme name'); return; }
    const base = THEMES[createBaseId] || allThemes[0];
    const id = `custom-${Date.now()}`;
    const newTheme = {
      id, isCustom: true,
      name: newThemeName.trim(),
      category: base.category,
      description: 'Custom theme',
      preview: { ...base.preview, accent: createAccent },
      vars: { ...base.vars, '--cb-accent': createAccent, '--cb-accent-h': createAccent },
    };
    saveUserTheme(newTheme);
    toast.success(`Theme "${newThemeName}" created`);
    setNewThemeName(''); setShowCreateForm(false);
  };

  return (
    <div className="min-h-screen p-6 md:p-8 pl-16 md:pl-10" style={{ background: 'var(--cb-bg)' }} data-testid="theme-marketplace-page">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: 'var(--cb-accent-dim)', border: '1px solid var(--cb-border)' }}>
              <Palette className="h-4 w-4" style={{ color: 'var(--cb-accent)' }} strokeWidth={1.5} />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight" style={{ color: 'var(--cb-text)' }}>Theme Marketplace</h1>
          </div>
          <p className="text-sm" style={{ color: 'var(--cb-text-dim)' }}>
            Hover to preview instantly. Click to apply. Changes persist across sessions.
          </p>
        </div>

        {/* Tab bar */}
        <div className="flex items-center gap-1 p-1 rounded-xl mb-8 w-fit" style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}>
          {[
            { id: 'themes', label: 'Themes', icon: Grid3X3 },
            { id: 'customize', label: 'Customize', icon: Sliders },
            { id: 'custom', label: `My Themes (${custom.length})`, icon: Star },
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150"
                style={{
                  background: activeTab === tab.id ? 'var(--cb-surface-a)' : 'transparent',
                  color: activeTab === tab.id ? 'var(--cb-text)' : 'var(--cb-text-dim)',
                }}
                data-testid={`theme-tab-${tab.id}`}
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ── THEMES TAB ─────────────────────────────────────────────────── */}
        {activeTab === 'themes' && (
          <div>
            {/* Dark themes */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <Moon className="h-3.5 w-3.5" style={{ color: 'var(--cb-text-dim)' }} strokeWidth={1.5} />
                <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--cb-text-dim)' }}>Dark Themes</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {builtIn.filter(t => t.category === 'dark').map(theme => (
                  <ThemeCard
                    key={theme.id}
                    theme={theme}
                    isActive={activeId === theme.id}
                    onApply={handleApply}
                    onPreview={previewTheme}
                    onLeave={clearPreview}
                  />
                ))}
              </div>
            </div>

            {/* Light themes */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Sun className="h-3.5 w-3.5" style={{ color: 'var(--cb-text-dim)' }} strokeWidth={1.5} />
                <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--cb-text-dim)' }}>Light Themes</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {builtIn.filter(t => t.category === 'light').map(theme => (
                  <ThemeCard
                    key={theme.id}
                    theme={theme}
                    isActive={activeId === theme.id}
                    onApply={handleApply}
                    onPreview={previewTheme}
                    onLeave={clearPreview}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── CUSTOMIZE TAB ─────────────────────────────────────────────── */}
        {activeTab === 'customize' && (
          <div className="space-y-8 max-w-xl">

            {/* Accent Color */}
            <div className="rounded-xl p-6" style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}>
              <div className="flex items-center gap-2 mb-5">
                <Palette className="h-4 w-4" style={{ color: 'var(--cb-accent)' }} strokeWidth={1.5} />
                <h3 className="font-semibold" style={{ color: 'var(--cb-text)' }}>Accent Color</h3>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {PRESET_ACCENTS.map(({ label, color }) => (
                  <button
                    key={color}
                    onClick={() => setCustomAccent(color)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150"
                    style={{
                      background: customAccent === color ? `${color}20` : 'var(--cb-surface-h)',
                      border: `1px solid ${customAccent === color ? color : 'var(--cb-border)'}`,
                      color: 'var(--cb-text-sec)',
                    }}
                    data-testid={`accent-preset-${label.toLowerCase()}`}
                  >
                    <div className="h-3 w-3 rounded-full flex-shrink-0" style={{ background: color }} />
                    {label}
                    {customAccent === color && <Check className="h-2.5 w-2.5" style={{ color }} />}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium" style={{ color: 'var(--cb-text-sec)' }}>Custom</label>
                <input
                  type="color"
                  value={customAccent || '#F5A623'}
                  onChange={e => setCustomAccent(e.target.value)}
                  className="h-9 w-14 rounded-lg cursor-pointer"
                  style={{ border: '1px solid var(--cb-border)', background: 'var(--cb-surface-h)' }}
                  data-testid="accent-color-picker"
                />
                <span className="text-xs font-mono" style={{ color: 'var(--cb-text-dim)' }}>
                  {customAccent || '#F5A623'}
                </span>
                {customAccent && (
                  <button
                    onClick={() => setCustomAccent(null)}
                    className="text-xs px-2 py-1 rounded"
                    style={{ background: 'var(--cb-surface-h)', color: 'var(--cb-text-dim)' }}
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>

            {/* Density */}
            <div className="rounded-xl p-6" style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}>
              <div className="flex items-center gap-2 mb-5">
                <Grid3X3 className="h-4 w-4" style={{ color: 'var(--cb-accent)' }} strokeWidth={1.5} />
                <h3 className="font-semibold" style={{ color: 'var(--cb-text)' }}>Density</h3>
              </div>
              <div className="flex gap-3">
                {['compact', 'comfortable'].map(mode => (
                  <button
                    key={mode}
                    onClick={() => setDensity(mode)}
                    className="flex-1 px-4 py-3 rounded-xl text-sm font-medium capitalize transition-all duration-150"
                    style={{
                      background: density === mode ? 'var(--cb-accent-dim)' : 'var(--cb-surface-h)',
                      border: `1px solid ${density === mode ? 'var(--cb-accent)' : 'var(--cb-border)'}`,
                      color: density === mode ? 'var(--cb-accent)' : 'var(--cb-text-sec)',
                    }}
                    data-testid={`density-${mode}`}
                  >
                    {mode}
                    {density === mode && <span className="ml-1 text-[10px]">✓</span>}
                  </button>
                ))}
              </div>
              <p className="text-xs mt-3" style={{ color: 'var(--cb-text-dim)' }}>
                Compact reduces padding for high-information density. Comfortable is optimized for readability.
              </p>
            </div>

            {/* Font Scale */}
            <div className="rounded-xl p-6" style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}>
              <div className="flex items-center gap-2 mb-5">
                <Type className="h-4 w-4" style={{ color: 'var(--cb-accent)' }} strokeWidth={1.5} />
                <h3 className="font-semibold" style={{ color: 'var(--cb-text)' }}>Font Scale</h3>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-xs font-medium" style={{ color: 'var(--cb-text-dim)' }}>80%</span>
                <input
                  type="range"
                  min="0.8"
                  max="1.2"
                  step="0.05"
                  value={fontScale}
                  onChange={e => setFontScale(parseFloat(e.target.value))}
                  className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer"
                  style={{ accentColor: 'var(--cb-accent)' }}
                  data-testid="font-scale-slider"
                />
                <span className="text-xs font-medium" style={{ color: 'var(--cb-text-dim)' }}>120%</span>
                <span className="text-sm font-semibold w-10 text-right" style={{ color: 'var(--cb-text)' }}>
                  {Math.round(fontScale * 100)}%
                </span>
              </div>
              <p className="text-xs mt-3" style={{ color: 'var(--cb-text-dim)' }}>
                Affects all text sizes globally. 100% is the default balanced size.
              </p>
            </div>

          </div>
        )}

        {/* ── MY THEMES TAB ─────────────────────────────────────────────── */}
        {activeTab === 'custom' && (
          <div>
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="mb-6 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150"
              style={{ background: 'var(--cb-accent)', color: 'black' }}
              data-testid="create-custom-theme-btn"
            >
              <Plus className="h-4 w-4" strokeWidth={2} />
              Create New Theme
            </button>

            {showCreateForm && (
              <div className="rounded-xl p-6 mb-6" style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}>
                <h3 className="font-semibold mb-4" style={{ color: 'var(--cb-text)' }}>New Custom Theme</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: 'var(--cb-text-sec)' }}>Theme Name</label>
                    <input
                      value={newThemeName}
                      onChange={e => setNewThemeName(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                      style={{ background: 'var(--cb-bg)', border: '1px solid var(--cb-border)', color: 'var(--cb-text)' }}
                      placeholder="My Custom Theme"
                      data-testid="custom-theme-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: 'var(--cb-text-sec)' }}>Base Theme</label>
                    <select
                      value={createBaseId}
                      onChange={e => setCreateBaseId(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                      style={{ background: 'var(--cb-bg)', border: '1px solid var(--cb-border)', color: 'var(--cb-text)' }}
                      data-testid="custom-theme-base-select"
                    >
                      {Object.values(THEMES).map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: 'var(--cb-text-sec)' }}>Accent Color</label>
                    <div className="flex items-center gap-3">
                      <input type="color" value={createAccent} onChange={e => setCreateAccent(e.target.value)}
                        className="h-9 w-14 rounded-lg cursor-pointer" style={{ border: '1px solid var(--cb-border)' }} />
                      <span className="text-xs font-mono" style={{ color: 'var(--cb-text-dim)' }}>{createAccent}</span>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={handleCreateTheme} className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: 'var(--cb-accent)', color: 'black' }} data-testid="save-custom-theme-btn">
                      Save Theme
                    </button>
                    <button onClick={() => setShowCreateForm(false)} className="px-4 py-2 rounded-lg text-sm" style={{ background: 'var(--cb-surface-h)', color: 'var(--cb-text-sec)' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {custom.length === 0 ? (
              <div className="text-center py-16 rounded-xl" style={{ background: 'var(--cb-surface)', border: '1px solid var(--cb-border)' }}>
                <Palette className="h-12 w-12 mx-auto mb-4" style={{ color: 'var(--cb-text-dim)' }} strokeWidth={1} />
                <p className="font-medium mb-1" style={{ color: 'var(--cb-text-sec)' }}>No custom themes yet</p>
                <p className="text-sm" style={{ color: 'var(--cb-text-dim)' }}>Create a theme above to get started</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {custom.map(theme => (
                  <div key={theme.id} className="relative">
                    <ThemeCard
                      theme={theme}
                      isActive={activeId === theme.id}
                      onApply={handleApply}
                      onPreview={previewTheme}
                      onLeave={clearPreview}
                    />
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteUserTheme(theme.id); toast.success('Theme deleted'); }}
                      className="absolute top-2 right-2 h-6 w-6 rounded-full flex items-center justify-center transition-all opacity-0 hover:opacity-100 group-hover:opacity-100"
                      style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444' }}
                      data-testid={`delete-theme-${theme.id}`}
                    >
                      <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
